import { useEffect, useState } from "react"
import { currentTimeKey } from "./themes"

/**
 * useCollectionSectionToggle
 *
 * Drives a "which section is showing" toggle over a collection's sections. A
 * live signal (by default the time of day) auto-selects the matching section
 * and advances over time; a manual `select` pauses that clock until `resume`.
 *
 * Written for the DailyScene module first, but intentionally generic: any
 * collection whose sections map to a rotating signal (time of day, season,
 * calendar event) can reuse it. Sections only need a `key` to match on.
 */
export interface ToggleableSection {
  sectionKey: string
}

export interface CollectionSectionToggle<T extends ToggleableSection> {
  sections: readonly T[]
  /** The section currently shown (matched by sectionKey, falls back to the first). */
  active: T | null
  activeKey: string
  /** True while a manual selection is holding the live clock. */
  paused: boolean
  /** Manually show a section (pauses the live clock). */
  select: (key: string) => void
  /** Clear the manual selection and follow the live clock again. */
  resume: () => void
}

export interface ToggleOptions {
  /** Deterministic starting key (stories/tests); otherwise the live signal. */
  initialKey?: string
  /** The live signal. Defaults to the time-of-day bucket. */
  signal?: () => string
  /** How often to re-check the live signal while playing. */
  intervalMs?: number
}

export function useCollectionSectionToggle<T extends ToggleableSection>(
  sections: readonly T[],
  options: ToggleOptions = {}
): CollectionSectionToggle<T> {
  const signal = options.signal ?? (() => currentTimeKey())
  const [liveKey, setLiveKey] = useState<string>(options.initialKey ?? signal())
  const [override, setOverride] = useState<string | null>(null)
  const paused = override != null

  useEffect(() => {
    if (paused) return
    const id = setInterval(() => setLiveKey(signal()), options.intervalMs ?? 60_000)
    return () => clearInterval(id)
    // signal is stable enough for our use; re-run only when play/pause flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused])

  const activeKey = override ?? liveKey
  const active =
    sections.find((section) => section.sectionKey === activeKey) ?? sections[0] ?? null

  return {
    sections,
    active,
    activeKey: active?.sectionKey ?? activeKey,
    paused,
    select: (key: string) => setOverride(key),
    resume: () => setOverride(null),
  }
}
